import styled, { keyframes } from 'styled-components';

const slideDown = keyframes`
  from { transform: translate(-50%, -120%); opacity: 0; }
  to   { transform: translate(-50%, 0);     opacity: 1; }
`;

// Banner fixo no topo-centro da viewport. Pill-shape compacto match
// design X/Twitter. Theme-aware: fundo claro no light, escuro no dark
// (antes era dark-forced e destoava do app claro). z-index 9999 pra
// ficar acima de modais — atualizacao de versao e a unica acao que
// justifica sobrepor o que esta sendo trabalhado.
//
// color: var(--cco-ink) funciona nos dois temas E nas duas
// severidades, porque o fundo tambem alterna por tema:
//   info  light: bg claro  + texto escuro
//   info  dark:  bg escuro + texto claro
//   crit  light: bg vermelho-claro + texto escuro
//   crit  dark:  bg vermelho-escuro + texto claro
//
// $severity: 'info' (cyan, dismissivel) ou 'critical' (vermelho, sem
// dismiss — backend exige FE mais nova).
export const Banner = styled.div`
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translate(-50%, 0);
  z-index: 9999;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 14px 10px 16px;
  background: ${(props) => {
    const isLight = props.theme.title === 'light';
    if (props.$severity === 'critical') {
      return isLight ? 'rgba(253, 236, 236, 0.98)' : 'rgba(40, 12, 12, 0.97)';
    }
    return isLight ? 'rgba(255, 255, 255, 0.98)' : 'rgba(15, 20, 33, 0.96)';
  }};
  border: 1px solid
    ${(props) =>
      props.$severity === 'critical'
        ? 'rgba(235, 109, 112, 0.6)'
        : 'rgba(78, 161, 243, 0.4)'};
  border-radius: 999px;
  box-shadow:
    0 0 0 1px
      ${(props) =>
        props.$severity === 'critical'
          ? 'rgba(235, 109, 112, 0.12)'
          : 'rgba(78, 161, 243, 0.08)'}
      inset,
    ${(props) =>
      props.theme.title === 'light'
        ? '0 8px 24px rgba(15, 23, 42, 0.16)'
        : '0 8px 28px rgba(0, 0, 0, 0.5)'};
  color: var(--cco-ink);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.01em;
  animation: ${slideDown} 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  max-width: calc(100vw - 32px);

  @media (max-width: 720px) {
    top: 8px;
    padding: 8px 10px 8px 12px;
    font-size: 12px;
    gap: 10px;
  }
`;

export const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: ${(props) =>
    props.$severity === 'critical' ? '#EB6D70' : 'var(--cco-blue)'};
  box-shadow: 0 0 0 4px
    ${(props) =>
      props.$severity === 'critical'
        ? 'rgba(235, 109, 112, 0.22)'
        : 'rgba(78, 161, 243, 0.18)'};
  flex-shrink: 0;
  ${(props) =>
    props.$severity === 'critical' &&
    `
    animation: pulse 1.6s ease-in-out infinite;
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
  `}
`;

export const Text = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 6px;

  > strong {
    font-weight: 700;
    color: ${(props) =>
      props.$severity === 'critical' ? '#EB6D70' : 'var(--cco-blue)'};
    font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, Consolas,
      monospace;
    font-size: 12px;
  }

  @media (max-width: 480px) {
    > .label {
      display: none;
    }
  }
`;

export const ReloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${(props) =>
    props.$severity === 'critical' ? '#EB6D70' : 'var(--cco-blue)'};
  color: #fff;
  border: none;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: background 0.15s, transform 0.15s;
  font-family: inherit;

  > svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
    stroke-width: 2.2;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:hover {
    background: ${(props) =>
      props.$severity === 'critical' ? '#d75a5d' : '#3a8edc'};
    transform: scale(1.03);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.$severity === 'critical'
          ? 'rgba(235, 109, 112, 0.45)'
          : 'rgba(78, 161, 243, 0.35)'};
  }

  &:active {
    transform: scale(0.97);
  }
`;

export const DismissButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  color: var(--cco-ink-mute);
  transition: background 0.15s, color 0.15s;

  > svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    color: var(--cco-ink);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(78, 161, 243, 0.35);
  }
`;
